<template>
  <div
    class="sider-menu"
    :class="isExpanded ? 'sider-menu__expanded' : 'sider-menu__collapsed'"
    style="position: relative"
  >
    <div id="sider-menu__dummy-wrapper tw:overflow-y-auto">
      <div
        @mouseleave="toggleHovered(false)"
        :class="{
          'sider-menu__expanded sider-menu__opened': isOpened,
          'sider-menu__collapsed sider-menu__closed': !isOpened,
        }"
        class="sider-menu__content sider-animation"
      >
        <div
          ref="scrollable_wrapper"
          style="height: 100%; width: 100%"
          @mouseenter="toggleHovered(true)"
          :class="{ 'tw:overflow-y-auto': isOpened }"
          class="pb-0 d-flex flex-column"
        >
          <div class="tw:bg-white tw:z-2">
            <v-list nav dense style="text-align: left">
              <v-list-item-group mandatory :value="currentSubpage" color="primary">
                <template v-for="(item, i) in subpages">
                  <v-list-item
                    :key="i"
                    class="sider-list-item"
                    @change="handleChangeTab(item.value)"
                    :input-value="item.value === currentSubpage"
                    :value="item.value"
                  >
                    <v-list-item-icon>
                      <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content class="ml-2">
                      <v-list-item-title class="text-truncate">{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action v-if="!!subpageExpandComponent(item.value)" class="tw:my-0">
                      <base-button
                        type="text"
                        icon="mdi-chevron-right"
                        :color="item.value === currentSubpage ? 'primary' : undefined"
                        :class="{ 'tw:[&_.v-icon]:-rotate-90': expandedPanels.includes(i) }"
                        @click="$event => expandSubpage($event, i)"
                      />
                    </v-list-item-action>
                  </v-list-item>
                  <v-expand-transition v-if="!!subpageExpandComponent(item.value)" :key="`${i}-expand`">
                    <component v-if="expandedPanels.includes(i)" :is="subpageExpandComponent(item.value)" />
                  </v-expand-transition>
                </template>
              </v-list-item-group>
            </v-list>
            <v-divider class="mt-2" />
          </div>
          <div class="flex-grow-1 py-4 pb-4 tw:overflow-y-hidden tw:shrink-0 tw:min-h-0">
            <component v-if="subpageComponent" :is="subpageComponent" :is-sider-opened="isOpened" />
          </div>
        </div>
        <span class="hide-sider-button">
          <base-button
            type="alternative"
            size="sm"
            class="tw:shadow-md tw:z-4"
            :icon="isExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'"
            :tooltip="isExpanded ? $i18n.t('sidebar.collapse') : $i18n.t('sidebar.expand')"
            @click="toggleExpanded"
          />
        </span>
      </div>
    </div>
    <user-settings-dialog :is-visible.sync="isUserSettingsDialogVisible" v-if="isUserSettingsDialogVisible !== null" />
  </div>
</template>

<script>
import { sync, get, call } from 'vuex-pathify';
import { debounce } from 'lodash';
import SiderAdminSection from '@/components/SiderAdminSection';
import SiderMapSection from '@/components/SiderMapSection';
import SiderSheetsSection from '@/components/SiderSheetsSection.vue';
import SiderDashboardsSection from '@/components/SiderDashboardsSection';

export default {
  name: 'SiderMenu',
  data: () => ({
    isHovered: false,
    isExpanded: false,
    isUserSettingsDialogVisible: null,
    expandedPanels: [],
  }),
  computed: {
    sheetsModuleEnabled: get('admin/additionalModules@PAGE_TABLE_MODULE.enabled'),
    sheetsModulePermission: get('users/toolsPermissions@PAGE_TABLE_MODULE.main_value'),
    sheetsModuleVerboseName: get('admin/additionalModules@PAGE_TABLE_MODULE.verbose_name'),
    offersModuleEnabled: get('admin/additionalModules@OFFERS_MODULE.enabled'),
    offersModuleConfigured: get('admin/additionalModules@OFFERS_MODULE.configured'),
    offersModulePermission: get('users/toolsPermissions@OFFERS_MODULE.main_value'),
    offersModuleVerboseName: get('admin/additionalModules@OFFERS_MODULE.verbose_name'),
    tentecModuleEnabled: get('admin/additionalModules@TENTEC.enabled'),
    tentecModuleConfigured: get('admin/additionalModules@TENTEC.configured'),
    tentecModulePermission: get('users/toolsPermissions@TENTEC.main_value'),
    tentecModuleVerboseName: get('admin/additionalModules@TENTEC.verbose_name'),
    isExpandedState: sync('sider/isExpanded'),
    currentUser: get('users/currentUser'),
    currentTab: sync('sidebar/currentTab'),

    ordersModuleVerboseName: get('admin/additionalModules@ORDERS_MODULE.verbose_name'),
    isOrdersModuleEnabled: get('admin/additionalModules@ORDERS_MODULE.enabled'),
    isOrdersModuleConfigured: get('admin/additionalModules@ORDERS_MODULE.configured'),
    ordersModulePermission: get('users/toolsPermissions@ORDERS_MODULE.main_value'),
    isOrdersSettingEnabled: get('admin/settingsValues@orders_module_enabled'),
    isOrdersVisible() {
      return (
        this.isOrdersModuleEnabled &&
        this.ordersModulePermission &&
        this.isOrdersModuleConfigured &&
        this.isOrdersSettingEnabled
      );
    },
    isDashboardsModuleEnabled: get('admin/additionalModules@DASHBOARD.enabled'),
    isDashboardsModuleConfigured: get('admin/additionalModules@DASHBOARD.configured'),
    dashboardsModulePermission: get('users/toolsPermissions@DASHBOARD.main_value'),
    isDashboardsVisible() {
      return this.isDashboardsModuleEnabled && this.dashboardsModulePermission && this.isDashboardsModuleConfigured;
    },
    ozeModuleEnabled: get('admin/additionalModules@OZE_MODULE.enabled'),
    ozeModulePermissions: get('users/toolsPermissions@OZE_MODULE.main_value'),
    ozeSettingEnabled: get('admin/settingsValues@oze_module_enabled'),
    isProjectsVisible() {
      return this.ozeModuleEnabled && this.ozeSettingEnabled && this.ozeModulePermissions;
    },

    isOpened() {
      return this.isHovered || this.isExpanded;
    },
    currentSubpage() {
      return this.$route.meta.siderSubpageName || this.$route.path?.split('/')[1];
    },
    subpageComponent() {
      switch (this.currentSubpage) {
        case 'admin':
          return SiderAdminSection;
        default:
          return null;
      }
    },
    subpages() {
      return [
        ...(this.isDashboardsVisible
          ? [{ value: 'dashboards', text: this.$i18n.t('dashboards.dashboards'), icon: 'mdi-view-dashboard-outline' }]
          : []),
        ...(this.currentUser.is_admin ? [{ value: 'data', text: this.$i18n.t('sider.data'), icon: 'mdi-layers' }] : []),
        ...(this.sheetsModuleEnabled && this.sheetsModulePermission
          ? [{ value: 'sheets', text: this.sheetsModuleVerboseName, icon: 'mdi-file-table' }]
          : []),
        { value: 'map', text: this.$i18n.t('sider.maps'), icon: 'mdi-map-outline' },
        ...(this.isOrdersVisible
          ? [{ value: 'tasks', text: this.ordersModuleVerboseName, icon: 'mdi-format-list-checks' }]
          : []),
        ...(this.isProjectsVisible
          ? [{ value: 'projects', text: this.$i18n.t('projects.title'), icon: 'mdi-finance' }]
          : []),
        ...(this.offersModuleEnabled && this.offersModuleConfigured && this.offersModulePermission
          ? [{ value: 'offers', text: this.offersModuleVerboseName, icon: 'mdi-storefront-outline' }]
          : []),
        ...(this.tentecModuleEnabled && this.tentecModuleConfigured && this.tentecModulePermission
          ? [
              {
                value: 'tentec_import',
                text: `${this.tentecModuleVerboseName} ${this.$i18n.t('default.import')}`,
                icon: 'mdi-file-import-outline',
              },
            ]
          : []),
        ...(this.tentecModuleEnabled && this.tentecModuleConfigured && this.tentecModulePermission
          ? [
              {
                value: 'tentec_export',
                text: `${this.tentecModuleVerboseName} ${this.$i18n.t('default.export')}`,
                icon: 'mdi-file-export-outline',
              },
            ]
          : []),
        ...(this.currentUser.is_admin
          ? [{ value: 'admin', text: this.$i18n.t('sider.admin'), icon: 'mdi-account-cog' }]
          : []),
      ];
    },
  },
  methods: {
    createGtagEvent: call('gtag/createEvent'),
    subpageExpandComponent(subpage) {
      switch (subpage) {
        case 'map':
          return SiderMapSection;
        case 'sheets':
          return SiderSheetsSection;
        case 'dashboards':
          return SiderDashboardsSection;
        default:
          return null;
      }
    },
    handleChangeTab(tab) {
      switch (tab) {
        case 'sheets': {
          this.$router.push({ name: 'sheetsList' });
          break;
        }
        case 'map': {
          this.currentTab = 0;
          this.$router.push({ name: 'maps' });
          break;
        }
        case 'data': {
          this.$root.$emit('deactivateAllTools');
          this.$router.push({ name: 'data' });
          break;
        }
        case 'tasks': {
          this.$router.push({ name: 'tasksView' });
          break;
        }
        case 'projects': {
          this.$router.push({ name: 'projects' });
          break;
        }
        case 'offers': {
          this.$router.push({ name: 'offers' });
          break;
        }
        case 'dashboards': {
          this.$router.push({ name: 'dashboards' });
          break;
        }
        case 'tentec_import': {
          this.$router.push({ name: 'tentec_import' });
          break;
        }
        case 'tentec_export': {
          this.$router.push({ name: 'tentec_export' });
          break;
        }
        case 'admin': {
          this.$root.$emit('deactivateAllTools');
          this.$router.push({ name: this.$_config.defaultAdminTab });
          this.createGtagEvent({
            action: 'open_admin_view',
            category: 'map_navbar',
          });
          break;
        }
        default:
          break;
      }
    },
    handleSettingAction(setting) {
      setting;
    },
    toggleHovered(expand) {
      this.isHovered = expand;
    },
    expandSubpage(evt, i) {
      evt.stopPropagation();
      if (this.expandedPanels.includes(i)) this.expandedPanels = [...this.expandedPanels].filter(idx => idx !== i);
      else this.expandedPanels = [...this.expandedPanels, i];
    },
    toggleExpanded() {
      const wrapper = document.querySelector('#sider-menu__dummy-wrapper');
      wrapper?.classList.add('disable-animation');
      this.isExpanded = !this.isExpanded;
      if (this.currentSubpage !== 'admin') this.isExpandedState = this.isExpanded;
      if (!this.isExpanded) this.toggleHovered(false);
      setTimeout(() => wrapper?.classList.remove('disable-animation'), 0);
    },
  },
  watch: {
    isOpened(nV) {
      if (nV) return;
      if (this.$refs.scrollable_wrapper.scrollTop > 0) this.$refs.scrollable_wrapper.scrollTop = 0;
      this.expandedPanels = [];
    },
    currentSubpage: {
      handler(nV) {
        this.isExpanded = nV === 'admin' || this.isExpandedState;
      },
      immediate: true,
    },
  },
  created() {
    this.toggleHovered = debounce(this.toggleHovered, 350);
  },
};
</script>

<style scoped>
@reference '@/tailwind.css';

.sider-menu__content :deep(.sider-list-item) {
  @apply tw:p-0!;
}

.sider-menu__opened :deep(.sider-list-item) {
  @apply tw:px-2!;
}

.sider-menu__closed :deep(.v-list) {
  @apply tw:px-1!;
}

.sider-menu__opened :deep(.v-list) {
  @apply tw:px-1.5!;
}

.sider-menu__closed :deep(.v-list-item__action) {
  @apply tw:hidden!;
}

:deep(.sider-list-item:not(.v-list-item--active):not(.sider-list-item-disabled) *:not(.v-icon)) {
  @apply tw:text-gray-800!;
}

:deep(.sider-list-item:not(.v-list-item--active):not(.sider-list-item-disabled) .v-icon) {
  @apply tw:text-gray-800!;
}
</style>

<style lang="scss" scoped>
:deep(.sider-list-item .v-list-item__icon:first-of-type) {
  display: flex;
  justify-content: center;
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 40px !important;
  min-width: 40px !important;
}

:deep(.sider-list-item),
:deep(.sider-list-item *) {
  transition: all 0.2s ease;
}

:deep(.sider-list-item *:not(.v-icon)) {
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
}

.sider-menu {
  z-index: 3;
  height: 100%;
  flex-shrink: 0;
  position: relative;
  background-color: #fff;
  min-width: 0;
}
:deep(.sider-animation) {
  transition: all 0.2s ease;
}
:deep(.disable-animation),
:deep(.disable-animation *) {
  transition: none !important;
}
.sider-menu__content {
  height: 100%;
  background-color: #fff;
  position: absolute;
  top: 0;
  left: 0;
  @include border(right, 1px);
}
.sider-menu__expanded {
  width: $sider-menu__expanded !important;
}
.sider-menu__collapsed {
  width: $sider-menu__collapsed !important;
}
.hide-sider-button {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(50%, -50%);
  z-index: 3;
}
</style>
